import { Activity, AlertTriangle, CheckCircle2, CircleDot, Loader2 } from 'lucide-react';
import { Badge, cn } from '@staff-app/ui';
import type { TroubleshootSession } from '../hooks/useTroubleshoot';
import { StaffSessionBadge } from './StaffSessionBadge';

const ACTIVE = new Set(['creating', 'running']);

/** "<device> — <title>" (scio3 `sessionLabel`), else whichever exists, else the UUID. */
export function sessionLabel(s: Pick<TroubleshootSession, 'deviceName' | 'title' | 'deviceUUID'>): string {
  const parts = [s.deviceName, s.title].filter((p): p is string => Boolean(p && p.trim()));
  return parts.length ? parts.join(' — ') : s.deviceUUID;
}

/** Compact h/m/s between start and end; "—" while open or unknown (scio3 `formatDuration`). */
export function formatSessionDuration(startIso?: string | null, endIso?: string | null): string {
  const start = startIso ? Date.parse(startIso) : NaN;
  const end = endIso ? Date.parse(endIso) : NaN;
  if (Number.isNaN(start) || Number.isNaN(end)) return '—';
  const s = Math.max(0, Math.round((end - start) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

const STATUS_LABEL: Record<string, string> = {
  creating: 'Starting',
  running: 'Active',
  paused: 'Paused',
  ended: 'Ended',
  failed: 'Failed',
};

function formatStarted(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function StatusIcon({ status }: { status: string }) {
  const Icon =
    status === 'failed'
      ? AlertTriangle
      : ACTIVE.has(status)
        ? Activity
        : status === 'ended'
          ? CheckCircle2
          : CircleDot;
  return (
    <Icon
      aria-hidden
      className={cn(
        'mt-0.5 h-4 w-4 shrink-0',
        status === 'failed'
          ? 'text-destructive'
          : ACTIVE.has(status)
            ? 'text-primary'
            : 'text-muted-foreground',
      )}
    />
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'failed' ? 'destructive' : ACTIVE.has(status) ? 'default' : 'secondary';
  return (
    <Badge variant={variant} className="shrink-0 text-[10px]">
      {STATUS_LABEL[status] ?? status}
    </Badge>
  );
}

/**
 * scio3 `DeviceTroubleshootSessionList` for staff — one row per session: status icon ·
 * "<device> — <title>" · "Staff" (staff-started) · status badge, then "started · by <actor> · duration". A row opens its
 * transcript (or the live view, for the session this browser is streaming).
 */
export function DeviceSessionList({
  sessions,
  loading,
  onSelect,
}: {
  sessions: TroubleshootSession[];
  loading?: boolean;
  onSelect: (sessionId: string) => void;
}) {
  if (loading && sessions.length === 0) {
    return (
      <div className="flex items-center justify-center p-6" role="status" aria-label={'Loading sessions'}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (sessions.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="troubleshoot-empty">
        {'No troubleshooting sessions for this device yet.'}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border" data-testid="troubleshoot-session-list">
      {sessions.map((s) => (
        <li key={s.sessionId}>
          <button
            type="button"
            onClick={() => onSelect(s.sessionId)}
            className="flex w-full cursor-pointer items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-panel-hover"
            data-testid="troubleshoot-session-row"
          >
            <StatusIcon status={s.status} />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{sessionLabel(s)}</span>
                <StaffSessionBadge mode={s.mode} />
                <StatusBadge status={s.status} />
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {`${formatStarted(s.startedAt)} · by ${s.actor} · ${formatSessionDuration(s.startedAt, s.endedAt)}`}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
